import { Component, ViewChild } from '@angular/core';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { DataTableDirective } from 'angular-datatables';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { Subject, Subscription } from 'rxjs';
import { ColumnData, DataTableRequest, DataTableResult } from '../../services/api-services';
import { ConfigColumns } from 'datatables.net';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { ValidationHandlerService } from '../../services/validation-handler.service';
@Component({
    template: '',
})
export class BaseComponent {
    private isSpinnerVisible: boolean = false;

    @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;
    dtOptions: ADTSettings = {};
    dtTrigger: Subject<ADTSettings>;
    showTable: boolean = true;

    public datatableRequest: DataTableRequest;
    public searchValue: string;

    public languageSubscription: Subscription;
    public languageUrl: string;
    private currentLanguage: string = '';

    constructor(protected spinnerService: NgxSpinnerService, protected modalService: BsModalService, protected validationHandlerService: ValidationHandlerService, protected translate?: TranslateService, protected languageService?: LanguageService) { }

    public dataTableOnInit() {
        this.dtTrigger = new Subject<ADTSettings>();
        setTimeout(() => {
            this.dtOptions = this.getDtOptions();
        });

        this.languageSubscription = this.languageService.language$.subscribe(value => {
            if (this.currentLanguage !== value) {
                this.currentLanguage = value;
                this.dtOptions = this.getDtOptions();
                this.reinitializeDataTable();
            }
        });
    }

    public dataTableAfterViewInit() {
        setTimeout(() => {
            this.dtOptions.columns.forEach(column => {
                if (column.title) {
                    column.title = this.translate.instant(column.title);
                }
            });
            if (!this.dtTrigger.closed) {
                this.dtTrigger.next(this.dtOptions);
            }
        });
    }

    public dataTableOnDestroy() {
        this.dtTrigger.unsubscribe();
        this.languageSubscription.unsubscribe();
    }

    // override
    public getDtOptions() {
        return this.dtOptions;
    }

    public transformToColumnData(columns: ConfigColumns[]): ColumnData[] {
        return columns.map(column => ({
            data: column.data + '',
            name: column.title,
            searchable: column.searchable ?? false,
            orderable: column.orderable ?? true,
        }));
    }

    public refreshDataTable() {
        if (!this.dtElement) {
            return;
        }
        this.dtElement?.dtInstance?.then((dtInstance: any) => {
            dtInstance.ajax.reload();
        });
    }

    public reinitializeDataTable() {
        if (!this.dtElement) {
            return;
        }
        this.dtElement?.dtInstance?.then((dtInstance: any) => {
            dtInstance.destroy();
            this.dtTrigger.unsubscribe();
            this.showTable = false;

            this.dtTrigger = new Subject<ADTSettings>();
            setTimeout(() => {
                this.showTable = true;
                setTimeout(() => {
                    this.dtOptions.columns.forEach(column => {
                        if (column.title) {
                            column.title = this.translate.instant(column.title);
                        }
                    });
                    if (!this.dtTrigger.closed) {
                        this.dtTrigger.next(this.dtOptions);
                    }
                });
            });
        });
    }

    public getTableData(res: DataTableResult) {
        return {
            draw: res.draw,
            recordsTotal: res.recordsTotal,
            recordsFiltered: res.recordsFiltered,
            data: res.data,
        };
    }

    public getEmptyTableData() {
        let emptyTableData: DataTableResult = {
            draw: 1,
            recordsTotal: 0,
            recordsFiltered: 0,
            data: [],
        };

        return emptyTableData;
    }

    public openConfirmDialog(itemName: string, onConfirm: Function = null, onCancel: Function = null, title: string = 'common.delete_confirm_title', confirmMessage: string = 'common.delete_confirm_message', cssClass: string = 'modal-lg') {
        const initialState: ModalOptions = {
            class: cssClass,
            initialState: {
                title: title,
                confirmMessage: confirmMessage,
                itemName: itemName,
            },
        };
        var bsModalRef = this.modalService.show(ConfirmDialogComponent, initialState);
        bsModalRef.onHide.subscribe((reason: string | any) => {
            if (reason === ConfirmDialogComponent.OK) {
                if (onConfirm) {
                    onConfirm();
                }
            } else {
                if (onCancel) {
                    onCancel();
                }
            }
        });
    }

    public getDefaultModalOptions(title: string, item?: any): any {
        let modalOptions: ModalOptions = {
            class: 'modal-lg',
            initialState: {
                title: title,
            },
        };
        if (item) {
            modalOptions.initialState['item'] = item;
        }
        return modalOptions;
    }

    protected setDivBackgroundImage(elementId: string, backgroundImageUrl: string) {
        var imgDiv = <HTMLElement>document.getElementById(elementId);
        imgDiv.style.backgroundImage = backgroundImageUrl;
    }

    protected setEmptyDivBackgroundImage(elementId: string) {
        this.setDivBackgroundImage(elementId, 'url("/assets/images/noimage.jpg")');
    }

    protected setImgSrc(elementId: string, textureUrl: string) {
        if (textureUrl == null) {
            textureUrl = '/assets/images/noimage.jpg';
        }
        var img = <HTMLImageElement>document.getElementById(elementId);
        img.src = textureUrl;
    }

    protected setEmptyImgSrc(elementId: string) {
        this.setImgSrc(elementId, '/assets/images/noimage.jpg');
    }

    protected setEmptyFileInputValue(elementId: string) {
        var textureSelectorInput = <HTMLInputElement>document.getElementById(elementId);
        textureSelectorInput.value = '';
    }

    protected isImageFileExtensionGood(file: File) {
        const allowedExtensions = ['jpg', 'jpeg', 'gif', 'png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        return fileExtension ? allowedExtensions.includes(fileExtension) : false;
    }


    protected isFileExtensionGood(file: File) {
        const allowedExtensions = ['pub', 'doc', 'docx', 'xlsx', 'ai', 'psd', 'eps', 'tif', 'zip', 'pdf', 'jpeg', 'jpg', 'png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        return fileExtension ? allowedExtensions.includes(fileExtension) : false;
    }



    protected isFileSizeGood(file: File) {
        const maxFileSize = 10 * 1024 * 1024; // 10 MB
        return file.size <= maxFileSize;
    }

    protected isImportFileExtensionGood(file: File) {
        const allowedExtensions = ['xlsx'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        return fileExtension ? allowedExtensions.includes(fileExtension) : false;
    }

    public handleSpinner(promise: Promise<any>): Promise<any> {
        if (!this.isSpinnerVisible) {
            this.isSpinnerVisible = true;
            this.spinnerService.show();
        }
        promise
            .then(res => {
                this.validationHandlerService.displayMessages(res);
                this.spinnerService.hide();
                this.isSpinnerVisible = false;
            })
            .catch(() => {
                this.spinnerService.hide();
                this.isSpinnerVisible = false;
            });

        return promise;
    }
}
